import React from 'react';
import { Id, toast } from 'react-toastify';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type ToastVariant = 'success' | 'error';

const ACCENT: Record<ToastVariant, string> = {
  success: '#3fb950',
  error: '#f85149',
};

const CARD_SX = {
  minWidth: 260,
  maxWidth: 460,
  px: 1.75,
  py: 1.5,
  borderRadius: 2.5,
  // VSCode / Slack 風のダークなガラス調カード
  bgcolor: 'rgba(30, 33, 39, 0.96)',
  color: '#e6edf3',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28), 0 2px 6px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(8px)',
} as const;

const IconBadge: React.FC<{ variant: ToastVariant; children: React.ReactNode }> = ({
  variant,
  children,
}) => {
  const accent = ACCENT[variant];
  return (
    <Box
      sx={{
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: '50%',
        color: accent,
        bgcolor: `${accent}1f`,
      }}
    >
      {children}
    </Box>
  );
};

/** 成功などシンプルなトースト（1行 + 任意で閉じるボタン） */
const ToastContent: React.FC<{
  icon: React.ReactNode;
  message: string;
  variant: ToastVariant;
  onClose?: () => void;
}> = ({ icon, message, variant, onClose }) => (
  <Stack direction="row" alignItems="center" spacing={1.25} sx={CARD_SX}>
    <IconBadge variant={variant}>{icon}</IconBadge>
    <Typography
      variant="body2"
      fontWeight={500}
      sx={{ flex: 1, lineHeight: 1.45, color: 'inherit', wordBreak: 'break-word' }}
    >
      {message}
    </Typography>
    {onClose && (
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="閉じる"
        sx={{
          flexShrink: 0,
          p: 0.25,
          color: 'rgba(230, 237, 243, 0.6)',
          '&:hover': { color: '#e6edf3', bgcolor: 'rgba(255, 255, 255, 0.08)' },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Stack>
);

/**
 * エラートースト。固定文言に加え、detail（レスポンスBodyのメッセージ）がある場合は
 * 「エラーの詳細」リンクを表示し、クリックで詳細を開閉できる。
 */
const ErrorToastContent: React.FC<{
  message: string;
  detail?: string;
  onClose: () => void;
}> = ({ message, detail, onClose }) => (
  <Box sx={CARD_SX}>
    <Stack direction="row" alignItems="flex-start" spacing={1.25}>
      <IconBadge variant="error">
        <ErrorRoundedIcon sx={{ fontSize: 18 }} />
      </IconBadge>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ lineHeight: 1.45, color: 'inherit', wordBreak: 'break-word' }}
        >
          {message}
        </Typography>
        {detail && detail !== message && (
          <Typography
            variant="caption"
            component="div"
            sx={{
              mt: 0.5,
              lineHeight: 1.5,
              color: 'rgba(230, 237, 243, 0.6)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {detail}
          </Typography>
        )}
      </Box>
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="閉じる"
        sx={{
          flexShrink: 0,
          mt: -0.25,
          p: 0.25,
          color: 'rgba(230, 237, 243, 0.6)',
          '&:hover': { color: '#e6edf3', bgcolor: 'rgba(255, 255, 255, 0.08)' },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  </Box>
);

const baseStyle = {
  background: 'transparent',
  boxShadow: 'none',
  padding: 0,
  margin: 0,
  minHeight: 'auto',
} as const;

/**
 * catch (e) の unknown なエラーから、Toast の「エラーの詳細」に表示する文字列を取り出す。
 * ApiClientError 系は message にレスポンスBodyの message が入っている。
 */
export const toErrorDetail = (e: unknown): string | undefined => {
  if (e instanceof Error && e.message) return e.message;
  if (typeof e === 'string' && e) return e;
  return undefined;
};

export const successDefault = (message: string) => {
  toast(
    <ToastContent
      icon={<CheckCircleRoundedIcon sx={{ fontSize: 18 }} />}
      message={message}
      variant="success"
    />,
    {
      position: 'bottom-left',
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: true,
      icon: false,
      closeButton: false,
      style: baseStyle,
    },
  );
};

/**
 * エラートーストを表示する。
 * @param message 固定文言（例: '保存に失敗しました'）
 * @param detail  任意。エラーレスポンスBodyのメッセージ等。渡すと「エラーの詳細」リンクで展開表示できる。
 *
 * 自動では閉じず、バツ（閉じる）ボタンでのみ閉じられる。
 */
export const errorDefault = (message: string, detail?: string) => {
  let toastId: Id;
  toastId = toast(
    <ErrorToastContent message={message} detail={detail} onClose={() => toast.dismiss(toastId)} />,
    {
      position: 'bottom-left',
      autoClose: false,
      hideProgressBar: true,
      closeOnClick: false,
      icon: false,
      closeButton: false,
      draggable: false,
      style: baseStyle,
    },
  );
};
